import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Grid,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { ApiResponse } from '../../models/apiResponse';
import { checkSession } from '../../services/authService';

export const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

interface ApiStatusResult {
  successRate: number;
  failureRate: number;
  averageComputationTime: number;
}

interface ApiPathStatus {
  path: string;
  name: string;
  status?: ApiStatusResult;
  loading: boolean;
}

interface ApiCategory {
  name: string;
  description: string;
  paths: ApiPathStatus[];
}

const ApiStatus: React.FC = () => {
  const [categories, setCategories] = useState<ApiCategory[]>([
    {
      name: '전국 초중고 시간표/급식/학사일정',
      description: '전국 모든 초중고의 시간표, 급식, 학사일정 정보를 제공합니다.',
      paths: [
        { path: '/schools/v2/search-school', name: '학교 검색', loading: true },
        { path: '/schools/v2/timetable', name: '시간표 조회', loading: true },
        { path: '/schools/v2/meal', name: '급식 조회', loading: true },
        { path: '/schools/v2/schedule', name: '학사일정 조회', loading: true },
      ],
    },
    {
      name: '영문 주소 변환',
      description: '국내 주소를 영문으로 변환하여 제공합니다.',
      paths: [
        { path: '/address/v1/translate', name: '주소 변환', loading: true },
      ],
    },
  ]);

  const fetchApiStatus = async (path: string) => {
    try {
      const session = await checkSession();
      const accessToken = session?.access_token;

      if (!accessToken) {
        throw new Error("No access token found");
      }

      const response = await fetch(`${apiBaseUrl}/admin/v1/apiStatus?path=${path}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch API status');
      }

      const data: ApiResponse<ApiStatusResult> = await response.json();
      return data.result;
    } catch (error) {
      console.error(`Error fetching status for ${path}:`, error);
      return null;
    }
  };

  const updatePathStatus = (categoryIndex: number, pathIndex: number, status: ApiStatusResult | null, loading: boolean) => {
    setCategories(prev => {
      const newCategories = [...prev];
      newCategories[categoryIndex] = {
        ...newCategories[categoryIndex],
        paths: [...newCategories[categoryIndex].paths]
      };
      newCategories[categoryIndex].paths[pathIndex] = {
        ...newCategories[categoryIndex].paths[pathIndex],
        status: status ?? undefined,
        loading
      };
      return newCategories;
    });
  };

  useEffect(() => {
    categories.forEach((category, categoryIndex) => {
      category.paths.forEach((pathInfo, pathIndex) => {
        fetchApiStatus(pathInfo.path).then(status => {
          updatePathStatus(categoryIndex, pathIndex, status, false);
        });
      });
    });
  }, []);

  const getStatusColor = (successRate: number) => {
    if (successRate >= 99) return 'success';
    if (successRate >= 95) return 'warning';
    return 'error';
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 'bold' }}>
        API 상태
      </Typography>

      {categories.map((category, index) => (
        <Accordion key={index} defaultExpanded sx={{ mb: 2, bgcolor: 'white', borderRadius: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" fontWeight="bold">
              {category.name}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {category.description}
            </Typography>
            <Grid container spacing={2}>
              {category.paths.map((pathInfo, pathIndex) => (
                <Grid item xs={12} key={pathIndex}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {pathInfo.name}
                        </Typography>
                        {pathInfo.loading ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="body2" color="text.secondary">
                              상태 확인 중...
                            </Typography>
                          </Box>
                        ) : pathInfo.status ? (
                          <Chip
                            label={`성공률 ${pathInfo.status.successRate}%`}
                            color={getStatusColor(pathInfo.status.successRate)}
                            size="small"
                          />
                        ) : (
                          <Chip
                            label="상태 확인 실패"
                            color="error"
                            size="small"
                          />
                        )}
                      </Box>
                      {pathInfo.loading ? (
                        <Box sx={{ display: 'flex', gap: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            응답시간: 확인 중...
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            실패율: 확인 중...
                          </Typography>
                        </Box>
                      ) : pathInfo.status ? (
                        <Box sx={{ display: 'flex', gap: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            평균 응답시간: {pathInfo.status.averageComputationTime.toFixed(1)}ms
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            실패율: {pathInfo.status.failureRate}%
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          상태 정보를 불러올 수 없습니다.
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default ApiStatus; 